import React, { forwardRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { defaultStyle } from "components/Modals";
import { createClient, findClient } from "api/resources/clients";
import { ProtectedPaths } from "routes";
import { grey } from "@mui/material/colors";
import CountrySelect from "components/CountrySelect";
import { CountryType } from "api/models/CountryType";
import PostAddIcon from "@mui/icons-material/PostAdd";

interface NewClientModalProps {
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

type Step = "selection" | "estonia" | "manual";
type ClientType = "BUSINESS" | "PRIVATE";

const NewClientModal = forwardRef<HTMLDivElement, NewClientModalProps>(
  ({ setOpenModal }, _) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [valid, setValid] = useState(false);
    const [step, setStep] = useState<Step>("selection");
    const [clientType, setClientType] = useState<ClientType>("BUSINESS");
    const [selectedCountry, setSelectedCountry] = useState<CountryType | null>(
      null,
    );
    const [formValues, setFormValues] = useState({
      companyName: "",
      registryCode: "",
      firstName: "",
      lastName: "",
      personalCode: "",
    });

    const isManualFormValid = () => {
      if (!selectedCountry) return false;

      if (clientType === "BUSINESS") {
        return (
          formValues.companyName.trim() !== "" &&
          formValues.registryCode.trim() !== ""
        );
      } else {
        return (
          formValues.firstName.trim() !== "" &&
          formValues.lastName.trim() !== "" &&
          formValues.personalCode.trim() !== ""
        );
      }
    };

    const handleEstoniaSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setLoading(true);

      const data = new FormData(event.currentTarget);
      const postData = {
        code: (data.get("code") as string).trim(),
      };

      findClient(postData)
        .then(({ data }) => {
          setLoading(false);
          const clientId = data.id;
          setOpenModal(false);
          navigate(`/${ProtectedPaths.Clients}/${clientId}`);
        })
        .catch((error) => {
          setLoading(false);
          console.log(error);
        });
    };

    const handleManualSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setLoading(true);

      const postData: any = {
        type: clientType,
        country: selectedCountry?.label || "",
      };

      if (clientType === "BUSINESS") {
        postData.companyName = formValues.companyName.trim();
        postData.registryCode = formValues.registryCode.trim();
      } else {
        postData.firstName = formValues.firstName.trim();
        postData.lastName = formValues.lastName.trim();
        postData.personalCode = formValues.personalCode.trim();
      }

      createClient(postData)
        .then(({ data }) => {
          setLoading(false);
          const clientId = data.id;
          setOpenModal(false);
          navigate(`/${ProtectedPaths.Clients}/${clientId}`);
        })
        .catch((error) => {
          setLoading(false);
          console.log(error);
        });
    };

    const resetFormValues = () => {
      setFormValues({
        companyName: "",
        registryCode: "",
        firstName: "",
        lastName: "",
        personalCode: "",
      });
    };

    const renderSelectionStep = () => (
      <>
        <Typography id="modal-modal-title" variant="h5" component="h5">
          Add a new client
        </Typography>
        <Typography
          id="modal-modal-description"
          component="p"
          sx={{ color: grey[700], mt: "0.6rem", mb: "2rem" }}
        >
          Choose how you want to add a new client
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              variant="outlined"
              fullWidth
              sx={{
                height: "140px",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
              onClick={() => setStep("estonia")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
              >
                <path d="M1 11H31V21H1z"></path>
                <path
                  d="M5,4H27c2.208,0,4,1.792,4,4v4H1v-4c0-2.208,1.792-4,4-4Z"
                  fill="#3271c9"
                ></path>
                <path
                  d="M5,20H27c2.208,0,4,1.792,4,4v4H1v-4c0-2.208,1.792-4,4-4Z"
                  transform="rotate(180 16 24)"
                  fill="#fff"
                ></path>
                <path
                  d="M27,4H5c-2.209,0-4,1.791-4,4V24c0,2.209,1.791,4,4,4H27c2.209,0,4-1.791,4-4V8c0-2.209-1.791-4-4-4Zm3,20c0,1.654-1.346,3-3,3H5c-1.654,0-3-1.346-3-3V8c0-1.654,1.346-3,3-3H27c1.654,0,3,1.346,3,3V24Z"
                  opacity=".15"
                ></path>
                <path
                  d="M27,5H5c-1.657,0-3,1.343-3,3v1c0-1.657,1.343-3,3-3H27c1.657,0,3,1.343,3,3v-1c0-1.657-1.343-3-3-3Z"
                  fill="#fff"
                  opacity=".2"
                ></path>
              </svg>
              <Typography
                variant="h6"
                sx={{
                  textTransform: "capitalize",
                  color: "text.primary",
                  lineHeight: "1.25rem",
                }}
              >
                Estonia
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textTransform: "none" }}
              >
                Add via Elering integration
              </Typography>
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="outlined"
              fullWidth
              disabled
              sx={{
                height: "140px",
                display: "flex",
                flexDirection: "column",
                opacity: "0.5",
                gap: 1,
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
              >
                <path fill="#fff" d="M1 12H31V20H1z"></path>
                <path
                  d="M5,4H27c2.208,0,4,1.792,4,4v5H1v-5c0-2.208,1.792-4,4-4Z"
                  fill="#922a35"
                ></path>
                <path
                  d="M5,19H27c2.208,0,4,1.792,4,4v5H1v-5c0-2.208,1.792-4,4-4Z"
                  transform="rotate(180 16 23.5)"
                  fill="#922a35"
                ></path>
                <path
                  d="M27,4H5c-2.209,0-4,1.791-4,4V24c0,2.209,1.791,4,4,4H27c2.209,0,4-1.791,4-4V8c0-2.209-1.791-4-4-4Zm3,20c0,1.654-1.346,3-3,3H5c-1.654,0-3-1.346-3-3V8c0-1.654,1.346-3,3-3H27c1.654,0,3,1.346,3,3V24Z"
                  opacity=".15"
                ></path>
                <path
                  d="M27,5H5c-1.657,0-3,1.343-3,3v1c0-1.657,1.343-3,3-3H27c1.657,0,3,1.343,3,3v-1c0-1.657-1.343-3-3-3Z"
                  fill="#fff"
                  opacity=".2"
                ></path>
              </svg>
              <Typography
                variant="h6"
                sx={{
                  textTransform: "capitalize",
                  color: "text.primary",
                  lineHeight: "1.25rem",
                }}
              >
                Latvia
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textTransform: "none" }}
              >
                Add via AST integration
              </Typography>
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              fullWidth
              sx={{
                height: "140px",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
              onClick={() => setStep("manual")}
            >
              <PostAddIcon fontSize="large" sx={{ color: "text.primary" }} />
              <Typography
                variant="h6"
                sx={{
                  textTransform: "capitalize",
                  color: "text.primary",
                  lineHeight: "1.25rem",
                }}
              >
                Manual
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textTransform: "none" }}
              >
                Add client manually
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </>
    );

    const renderEstoniaStep = () => (
      <>
        <Typography id="modal-modal-title" variant="h5" component="h5">
          Add a new client - Estonia
        </Typography>
        <Typography
          id="modal-modal-description"
          component="p"
          sx={{ color: grey[700], mt: "0.6rem" }}
        >
          Clicking "Add client" will fetch relevant data from Elering Datahub
          and create a new client in BMS.
        </Typography>
        <Box component="form" onSubmit={handleEstoniaSubmit} noValidate>
          <Grid container spacing={2} sx={{ mt: "1rem", mb: "2rem" }}>
            <Grid item sm={8}>
              <TextField
                id="code"
                name="code"
                label="Registry code or EIC"
                size="small"
                onChange={(e) =>
                  e.target.value !== "" ? setValid(true) : setValid(false)
                }
                required
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid item sm={12} sx={{ mt: "1rem" }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setStep("selection");
                  setValid(false);
                }}
                sx={{ mr: "1rem" }}
              >
                Back
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={loading}
                loadingIndicator="Loading…"
                disabled={loading || !valid}
              >
                Add client
              </LoadingButton>
            </Grid>
          </Grid>
        </Box>
      </>
    );

    const renderManualStep = () => (
      <>
        <Typography id="modal-modal-title" variant="h5" component="h5">
          Add a new client - Manual
        </Typography>
        <Typography
          id="modal-modal-description"
          component="p"
          sx={{ color: grey[700], mt: "0.6rem", mb: "1rem" }}
        >
          Fill in the client information manually
        </Typography>
        <Box component="form" onSubmit={handleManualSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CountrySelect
                label="Country"
                value={selectedCountry}
                setValue={setSelectedCountry}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Type</FormLabel>
                <RadioGroup
                  row
                  value={clientType}
                  onChange={(e) => setClientType(e.target.value as ClientType)}
                >
                  <FormControlLabel
                    value="BUSINESS"
                    control={<Radio />}
                    label="Business"
                  />
                  <FormControlLabel
                    value="PRIVATE"
                    control={<Radio />}
                    label="Private"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {clientType === "PRIVATE" ? (
              <>
                <Grid item xs={12}>
                  <TextField
                    id="personalCode"
                    name="personalCode"
                    label="Personal code"
                    size="small"
                    required
                    fullWidth
                    value={formValues.personalCode}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        personalCode: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="firstName"
                    name="firstName"
                    label="First name"
                    size="small"
                    required
                    fullWidth
                    value={formValues.firstName}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        firstName: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="lastName"
                    name="lastName"
                    label="Last name"
                    size="small"
                    required
                    fullWidth
                    value={formValues.lastName}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        lastName: e.target.value,
                      })
                    }
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <TextField
                    id="companyName"
                    name="companyName"
                    label="Company name"
                    size="small"
                    required
                    fullWidth
                    value={formValues.companyName}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        companyName: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="registryCode"
                    name="registryCode"
                    label="Registry code"
                    size="small"
                    required
                    fullWidth
                    value={formValues.registryCode}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        registryCode: e.target.value,
                      })
                    }
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sx={{ mt: "1rem" }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setStep("selection");
                  setSelectedCountry(null);
                  resetFormValues();
                }}
                sx={{ mr: "1rem" }}
              >
                Back
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={loading}
                loadingIndicator="Creating…"
                disabled={loading || !isManualFormValid()}
              >
                Create client
              </LoadingButton>
            </Grid>
          </Grid>
        </Box>
      </>
    );

    return (
      <Box sx={defaultStyle}>
        {step === "selection" && renderSelectionStep()}
        {step === "estonia" && renderEstoniaStep()}
        {step === "manual" && renderManualStep()}
      </Box>
    );
  },
);

export default NewClientModal;
